import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QrCodeModal } from '@pages/IntroducingBroker/Dashboard/ReferralLink/QrCodeModal.modal'
import { buildAliasShortUrl } from '@pages/IntroducingBroker/LinkHub/Links/helpers'

import { useModal } from '@context/ModalsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { CopyIcon } from '@ui/icons/Copy.icon'
import { QRCodeIcon } from '@ui/icons/QRCode.icon'
import { TrashIcon } from '@ui/icons/Trash.icon'
import { IconFCType } from '@ui/icons/types'

import { copyToClipboard } from '@utils/navigator.utils'

import { ReferralLinkAliasesDto } from '@model/ReferralUrlAliasDto'

import { ActionModal } from '@shared/modals/Action.modal'

export type AliasAction = {
  id: 'copy' | 'qr' | 'delete'
  icon: IconFCType
  label: string
  disabled?: boolean
  loading?: boolean
  onClick: () => void
}

export const useAliasActions = (
  alias: ReferralLinkAliasesDto,
  onDelete: (alias: ReferralLinkAliasesDto) => Promise<void>,
  ibCode: string
) => {
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()
  const [isDeleting, setIsDeleting] = useState(false)

  const aliasUrl = buildAliasShortUrl(ibCode, alias.alias)

  const qrCodeModal = useModal(QrCodeModal, {
    referralLink: aliasUrl,
    landingPageUrl: aliasUrl,
    isShortLinkLoading: false,
    isCustomUrl: true,
    ibCode,
    domain: window.location.host,
    generateShortenUrl: false,
  })

  const deleteModal = useModal(ActionModal, {
    title: t('IB.LinkHubPage.Delete alias'),
    description: t('IB.LinkHubPage.Are you sure you want to delete this alias?'),
    onConfirm: async () => {
      try {
        setIsDeleting(true)
        await onDelete(alias)
        addSnackbar.success({ message: t('IB.LinkHubPage.Alias deleted successfully') })
      } catch {
        addSnackbar.error({ message: t('Something went wrong! Please try again later') })
      } finally {
        setIsDeleting(false)
      }
    },
  })

  const copy = useCallback(() => {
    copyToClipboard(aliasUrl)
    addSnackbar.success({ message: t('Copied') })
  }, [aliasUrl, addSnackbar, t])

  const actions: AliasAction[] = [
    {
      id: 'copy',
      icon: CopyIcon,
      label: t('IB.LinkHubPage.Copy personal link'),
      disabled: !aliasUrl,
      onClick: copy,
    },
    {
      id: 'qr',
      icon: QRCodeIcon,
      label: t('QR'),
      disabled: !aliasUrl,
      onClick: () => qrCodeModal.open(),
    },
    {
      id: 'delete',
      icon: TrashIcon,
      label: t('Delete'),
      loading: isDeleting,
      disabled: isDeleting,
      onClick: () => deleteModal.open(),
    },
  ]

  return { actions }
}
